import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ReservationFormPrivateRoom } from './ReservationFormPrivateRoom';
import { ReservationFormServiceTypeSelector } from './ReservationFormServiceTypeSelector';
import { ReservationFormShowerRoom } from './ReservationFormShowerRoom';
import { useRestoreReservation } from '../../hooks/useRestoreReservation';
import { useUser } from '../../hooks/useUser';
import { ErrorNotice } from '../common/ErrorNotice';
import type { ReservationFormData } from './ReservationFormServiceTypeSelector';
import { SPACE_TYPE } from '@/constants/space';

export const ReservationForm = () => {
  const { t } = useTranslation();
  const [hasActiveReservation, setHasActiveReservation] = useState(false);
  // ユーザー情報を取得
  const { isAnaLoungeMember } = useUser();

  // URLパラメータからサービスタイプを取得
  const [searchParams] = useSearchParams();
  const initialServiceType = searchParams.get('service') || '';

  // 確認画面からの復元データを取得
  const [restoreData, setRestoreData] = useState(useRestoreReservation());

  const { register, watch, setValue } = useForm<ReservationFormData>({
    mode: 'onChange',
    defaultValues: {
      serviceType: initialServiceType,
    },
  });

  const selectedServiceType = watch('serviceType');

  // 初回のみ復元データからサービスタイプを復元
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (restoreData?.serviceType && !hasRestoredRef.current) {
      setValue('serviceType', restoreData.serviceType);
      hasRestoredRef.current = true;
    }
  }, [restoreData?.serviceType, setValue]);

  // サービスタイプが変更されたら復元データをクリア
  useEffect(() => {
    // selectedServiceTypeが空の場合（初期状態）はスキップ
    if (
      restoreData &&
      selectedServiceType &&
      selectedServiceType !== restoreData.serviceType
    ) {
      setRestoreData(undefined);
    }
  }, [selectedServiceType, restoreData]);

  return (
    <div className="page p-auth page_b">
      <main className="page__main">
        <div className="page__content">
          <section className="l-section">
            <div className="l-section__line">
              <div className="l-container">
                {/* ヒーロー */}
                <div className="l-hero">
                  <img
                    className="l-hero__img"
                    src="/Common_Title.jpg"
                    alt="LOUNGE RESERVATION"
                  />
                </div>

                {/* エラー表示 */}
                {hasActiveReservation && (
                  <ErrorNotice
                    message={t(
                      'errors.api.reservation.create.previous_reservation_not_completed',
                    )}
                  />
                )}

                {/* エリア群（グリッド） */}
                <div className="l-grid">
                  {/* ボックス1：サービス */}
                  <section className="l-card reservation-card">
                    <ReservationFormServiceTypeSelector
                      register={register}
                      selectedServiceType={selectedServiceType}
                      isAnaLoungeMember={isAnaLoungeMember}
                    />
                  </section>

                  {/* ボックス2：予約選択エリア */}
                  <section className="l-card">
                    <div className="l-card__body">
                      <div className="reservation-card__panel">
                        <div className="reservation-card__panel-lists">
                          {/* シャワールームのパネル */}
                          {selectedServiceType === SPACE_TYPE.SHOWER_ROOM && (
                            <div
                              className="reservation-card__panel-list reservation-card__panel-list--shower"
                              data-history-id="shower"
                            >
                              <ReservationFormShowerRoom
                                restoreData={restoreData}
                              />
                            </div>
                          )}

                          {/* 防音個室ブースのパネル */}
                          {selectedServiceType === SPACE_TYPE.PRIVATE_ROOM && (
                            <div
                              className="reservation-card__panel-list reservation-card__panel-list--booth"
                              data-history-id="booth"
                            >
                              <ReservationFormPrivateRoom
                                onActiveReservationChange={
                                  setHasActiveReservation
                                }
                                restoreData={restoreData}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};
